import { useCallback, useEffect, useId, useRef, useSyncExternalStore, type MutableRefObject } from "react";

/**
 * Shared "unsaved changes" guard used by editor screens.
 *
 * Ported from apps/console (monorepo). Pure React — no router APIs — so it
 * works with the react-router route tree the console mounts this feature in.
 *
 * DIVERGENCE from the monorepo version: the console has one editor per screen,
 * so it keeps a single `saveRef`. A Category screen has several independent
 * editors on it at once (name/icon, assigned BimClasses), so this version keeps
 * a registry: each editor reports its own dirty flag and save function,
 * `isDirtyRef` becomes "any editor dirty", and saving runs every dirty editor.
 * Fold this behaviour back into the monorepo module when this repo moves.
 *
 * Coverage:
 * - Switching list items in the side list -> "Save before leaving?" confirm
 *   (OK saves then navigates, Cancel navigates discarding changes).
 * - Closing / reloading the browser tab -> native beforeunload warning.
 *
 * In-app route navigation (menu, back button) is NOT guarded.
 */

/** Resolves `true` when the save succeeded, `false` when it failed. */
export type SaveFn = () => Promise<boolean>;

type Editor = {
    dirty: boolean;
    reset: (() => void) | null;
    save: SaveFn | null;
};

export type UnsavedChangesContext = {
    isDirtyRef: MutableRefObject<boolean>;
    resetRef: MutableRefObject<(() => void) | null>;
    saveRef: MutableRefObject<SaveFn | null>;
    editorsRef: MutableRefObject<Map<string, Editor>>;
    listenersRef: MutableRefObject<Set<() => void>>;
};

/** Create the shared refs in the component that owns both the list and the editor outlet. */
export function useUnsavedChangesRefs(): UnsavedChangesContext {
    const isDirtyRef = useRef(false);
    const resetRef = useRef<(() => void) | null>(null);
    const saveRef = useRef<SaveFn | null>(null);
    const editorsRef = useRef<Map<string, Editor>>(new Map());
    const listenersRef = useRef<Set<() => void>>(new Set());

    // Reverts every registered editor that supplied a reset function. Used by
    // screens whose Reset button lives outside the editor's own <form>, so the
    // native type="reset" no longer reaches it.
    resetRef.current = () => {
        for (const editor of [...editorsRef.current.values()]) editor.reset?.();
    };

    // Saves every editor that has pending changes; `false` if any of them failed.
    //
    // Sequential, not Promise.all: editors on the same screen can build their
    // payload from a shared query (Category does — both replace the whole
    // document), so one save has to land and refetch before the next reads it.
    saveRef.current = async () => {
        let ok = true;
        for (const editor of [...editorsRef.current.values()]) {
            if (!editor.dirty || !editor.save) continue;
            if (await editor.save() === false) ok = false;
        }
        return ok;
    };

    return { editorsRef, isDirtyRef, listenersRef, resetRef, saveRef };
}

/**
 * Subscribe to "is any registered editor dirty?" as render state.
 *
 * `isDirtyRef` is a ref so it can be read from event handlers without
 * re-rendering; a screen that renders one shared Save button for several
 * editors needs the same value as state to enable/disable that button.
 */
export function useIsDirty(context: UnsavedChangesContext | null | undefined): boolean {
    const listenersRef = context?.listenersRef;
    const isDirtyRef = context?.isDirtyRef;

    return useSyncExternalStore(
        useCallback(
            (onStoreChange) => {
                if (!listenersRef) return () => undefined;
                listenersRef.current.add(onStoreChange);
                return () => {
                    listenersRef.current.delete(onStoreChange);
                };
            },
            [listenersRef],
        ),
        () => isDirtyRef?.current ?? false,
    );
}

/**
 * Wire an editor into the guard: registers its dirty state and save function,
 * and warns on browser close/reload while there are unsaved changes.
 */
export function useUnsavedChangesEditor(
    context: UnsavedChangesContext | null | undefined,
    save: SaveFn | null | undefined,
    dirty: boolean = true,
    reset?: (() => void) | null,
) {
    const id = useId();

    const editorsRef = context?.editorsRef;
    const isDirtyRef = context?.isDirtyRef;
    const listenersRef = context?.listenersRef;

    useEffect(() => {
        if (!editorsRef || !isDirtyRef) return;

        function sync() {
            isDirtyRef.current = [...editorsRef.current.values()].some((editor) => editor.dirty);
            for (const listener of listenersRef?.current ?? []) listener();
        }

        editorsRef.current.set(id, { dirty, reset: reset ?? null, save: save ?? null });
        sync();

        return () => {
            editorsRef.current.delete(id);
            sync();
        };
    }, [dirty, editorsRef, id, isDirtyRef, listenersRef, reset, save]);

    useEffect(() => {
        if (!isDirtyRef) return;
        function onBeforeUnload(event: BeforeUnloadEvent) {
            if (!isDirtyRef.current) return;
            event.preventDefault();
            event.returnValue = "";
        }
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", onBeforeUnload);
        };
    }, [isDirtyRef]);
}

/**
 * Call before navigating away from an editor (e.g. clicking another list item).
 * Returns whether the navigation should proceed.
 */
export async function confirmUnsavedChanges(context: UnsavedChangesContext): Promise<boolean> {
    const { isDirtyRef, listenersRef, saveRef } = context;
    if (!isDirtyRef.current) return true;

    const wantsToSave = confirm("You have unsaved changes. Save before leaving?");
    if (wantsToSave) {
        const ok = await saveRef.current?.();
        if (ok === false) return false; // save failed — stay so the error is visible
    }
    isDirtyRef.current = false;
    for (const listener of listenersRef?.current ?? []) listener();
    return true;
}
